import React, {Component} from "react"
import {connect} from "react-redux"

import withBranding, {WithBranding} from "@irati/brand/withBranding"
import AuthActions from "@irati/auth/store/actions/AuthActions"
import authentication from "@irati/auth"
import Button from "@irati/ui/src/Button"
import {InjectIntl, injectIntl, FormattedMessage} from "@irati/utils/lang"
import brandingService from "@irati/brand/services/branding"
import defaultAuth from "@irati/auth/helpers/defaultAuth"
import {PlaceholderContainer} from "@irati/ui/src/Placeholder"
import {H2} from "@irati/ui/src/Headings"
import {Para} from "@irati/ui/src/Labels"
import Flex from "@irati/ui/src/Flex"
import Validation from "@irati/utils/validation"
import ValidationButton from "@irati/ui/src/FormItem/ValidationButton"
import {UpdateBrandingInput} from "@irati/types/ops"
import Header from "./Header"
import EmailContent from "./EmailContent"
import {MarginButton, TwoColorBackground, EmailPreviewContainer, PreviewTextContainer, StyledTextArea} from "../styled"

const WELCOME_EMAIL_KEY = "admin.components.welcomeEmail.default"

interface Props extends InjectIntl, WithBranding {
  useDefault?: boolean,
  updateAuth: () => Promise<any>,
  setIsStepCompletable: (value: boolean) => void
}

interface State {
  welcomeEmailDraft: string,
  editing?: boolean,
  saving?: Promise<void>
}

class Preview extends Component<Props, State> {
  state: State = {
    welcomeEmailDraft: "",
    editing: true
  }

  updateWelcomeEmail = () => {
    const {setIsStepCompletable, updateAuth} = this.props
    const tenant = authentication.userTenant()

    const data: UpdateBrandingInput = {
      businessId: tenant.uuid,
      teamId: tenant.rootTeamId,
      welcomeEmail: this.state.welcomeEmailDraft
    }

    const saving = brandingService.update(data).then(async() => {
      await Promise.all([setIsStepCompletable(true), updateAuth()])
      this.setState({editing: false})
    })

    this.setState({saving})
  }

  componentDidMount() {
    return brandingService.get({fetchPolicy: "network-only"}).then((branding) => {
      const welcomeEmailDraft = branding.welcomeEmail || this.getDefaultEmailString()
      const isDefaultEmail = welcomeEmailDraft === this.getDefaultEmailString()
      this.setState({welcomeEmailDraft, editing: isDefaultEmail})
    })
  }

  getDefaultEmailString = () => this.props.formatMessage({id: WELCOME_EMAIL_KEY})

  handleChange = (value: string) => {
    this.setState({welcomeEmailDraft: value})
  }

  handleReset = () => this.setState({welcomeEmailDraft: this.getDefaultEmailString()})

  emailValidationSchema = Validation.string()
    .required("platform.defaultValidation.required")
    .max(650, this.props.formatMessage({id: "platform.defaultValidation.maxLength"}, {count: 650}))
    .test(
      "notMatchDefault",
      this.props.formatMessage({id: "setup_tenant.steps.welcomeEmail.preview.emailValidationMessage"}),
      (value: string) => value !== this.getDefaultEmailString()
    )

  render() {
    const {useDefault, branding} = this.props
    const {welcomeEmailDraft, editing, saving} = this.state
    const {businessLogoUrl, primaryColour} = branding
    const {imageLink: defaultImageLink} = defaultAuth.userTenant.settings

    const showCustomLogo = defaultImageLink !== businessLogoUrl

    return (
      <>
        <Flex alignItems="center" justifyContent="space-between" margin="10px 0">
          <Para>
            <FormattedMessage id="setup_tenant.steps.welcomeEmail.preview.introCaption" />
          </Para>
          {!editing && (
            <Button primary onClick={() => this.setState({editing: true})}>
              <FormattedMessage id="setup_tenant.steps.welcomeEmail.preview.editMessageButton" />
            </Button>
          )}
        </Flex>
        <PlaceholderContainer padding="30px" margin="15px 0 0 0">
          <Header showCustomLogo={showCustomLogo} perkboxImageLink={defaultImageLink} customLogoLink={businessLogoUrl} />
          <TwoColorBackground color1={primaryColour}>
            <EmailPreviewContainer>
              <H2 tiny>
                <FormattedMessage id="setup_tenant.steps.welcomeEmail.preview.email.greeting" />
              </H2>
              {useDefault && <EmailContent />}
              {!useDefault && !editing && <EmailContent welcomeEmail={welcomeEmailDraft} />}
              {!useDefault && editing && (
                <Flex width="100%" direction="column" alignItems="center" justifyContent="space-between">
                  <PreviewTextContainer border>
                    <StyledTextArea
                      name="welcomeEmail"
                      label="Your message"
                      hideLabel
                      value={welcomeEmailDraft}
                      rows={8}
                      onChange={this.handleChange}
                      validation={this.emailValidationSchema} />
                    <Flex>
                      <MarginButton onClick={this.handleReset}>
                        <FormattedMessage id="setup_tenant.steps.welcomeEmail.preview.defaultMessageButton" />
                      </MarginButton>
                      <ValidationButton primary onClick={this.updateWelcomeEmail} load={saving}>
                        <FormattedMessage id="setup_tenant.steps.welcomeEmail.preview.saveMessageButton" />
                      </ValidationButton>
                    </Flex>
                  </PreviewTextContainer>
                </Flex>
              )}
            </EmailPreviewContainer>
          </TwoColorBackground>
        </PlaceholderContainer>
      </>
    )
  }
}

const mapDispatchToProps = (dispatch: any) => {
  return {
    updateAuth: () => dispatch(AuthActions.updateTenant())
  }
}

export default connect(
  null,
  mapDispatchToProps
)(injectIntl(withBranding(Preview)))
